/**
 * 📧 Mail Jobs Component
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Play, 
  Pause,
  Eye,
  Trash2,
  Calendar,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Copy,
  Layers
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import MultiCampaignCreator from '../MultiCampaign/MultiCampaignCreator';
import { useAuth } from '../../services/AuthService';
import MultiCampaignDashboard from '../MultiCampaign/MultiCampaignDashboard';

const MailJobs = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [jobToReuse, setJobToReuse] = useState(null);
  const [showMultiCampaignCreator, setShowMultiCampaignCreator] = useState(false);
  const [showMultiCampaignDashboard, setShowMultiCampaignDashboard] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch jobs and multi-campaigns
  const { data: jobs, isLoading } = useQuery(
    'jobs',
    async () => {
      const response = await axios.get('/api/jobs');
      return response.data.data;
    },
    {
      refetchInterval: 10000, // Refresh every 10 seconds
      refetchOnWindowFocus: false
    }
  );

  // Fetch multi-campaigns
  const { data: multiCampaigns } = useQuery(
    'multiCampaigns',
    async () => {
      const response = await axios.get('/api/multi-campaigns');
      return response.data.data;
    },
    {
      refetchInterval: 10000, // Refresh every 10 seconds
      refetchOnWindowFocus: false
    }
  );

  // Group jobs by multi-campaign and combine with regular jobs
  const combinedItems = React.useMemo(() => {
    const items = [];
    let campaignGroups = {};
    
    console.log('🔍 Jobs data:', jobs?.length || 0, 'jobs');
    console.log('🔍 Multi-campaigns data:', multiCampaigns?.length || 0, 'campaigns');
    
    if (jobs) {
      // 🚀 Trenne bereits aggregierte Multi-Campaign Jobs von regulären Jobs
      const aggregatedMultiCampaigns = jobs.filter(job => job.is_aggregated && job.multi_campaign_id);
      const multiCampaignJobs = jobs.filter(job => job.multi_campaign_id && !job.is_aggregated);
      const regularJobs = jobs.filter(job => !job.multi_campaign_id);
      
      console.log('🔍 Aggregated multi-campaigns:', aggregatedMultiCampaigns.length);
      console.log('🔍 Multi-campaign jobs (not aggregated):', multiCampaignJobs.length);
      console.log('🔍 Regular jobs:', regularJobs.length);
      
      // 🚀 Bereits aggregierte Multi-Campaigns direkt verwenden (NICHT nochmal gruppieren!)
      aggregatedMultiCampaigns.forEach(job => {
        const campaignId = job.multi_campaign_id;
        campaignGroups[campaignId] = {
          type: 'multi-campaign-group',
          campaign_id: campaignId,
          slots: [], // Keine einzelnen Slots, da bereits aggregiert
          total_recipients: job.total_recipients,
          total_sent: job.sent_count,
          total_failed: job.failed_count,
          created_at: job.created_at,
          status: job.status,
          slot_count: job.slot_count, // Anzahl Slots aus API
          is_aggregated: true
        };
      });
      
      // Group non-aggregated multi-campaign jobs (falls vorhanden - Legacy-Support)
      multiCampaignJobs.forEach(job => {
        const campaignId = job.multi_campaign_id;
        if (!campaignGroups[campaignId]) {
          campaignGroups[campaignId] = {
            type: 'multi-campaign-group',
            campaign_id: campaignId,
            slots: [],
            total_recipients: 0,
            total_sent: 0,
            total_failed: 0,
            created_at: job.created_at,
            status: 'running',
            is_aggregated: false
          };
        }
        campaignGroups[campaignId].slots.push(job);
        campaignGroups[campaignId].total_recipients += (job.total_recipients || 0);
        campaignGroups[campaignId].total_sent += (job.sent_count || 0);
        campaignGroups[campaignId].total_failed += (job.failed_count || 0);
      });
      
      console.log('🔍 Campaign groups:', Object.keys(campaignGroups).length);
      
      // Calculate overall status for each campaign group
      Object.values(campaignGroups).forEach(group => {
        // 🚀 Bei aggregierten Daten Status direkt verwenden
        if (!group.is_aggregated && group.slots.length > 0) {
        const statuses = group.slots.map(slot => slot.status);
        if (statuses.every(s => s === 'completed')) {
          group.status = 'completed';
        } else if (statuses.some(s => s === 'processing' || s === 'running')) {
          group.status = 'running';
        } else if (statuses.some(s => s === 'failed')) {
          group.status = 'failed';
        } else {
          group.status = 'pending';
        }
        }
        // Bei aggregierten Daten ist group.status bereits gesetzt
        
        // Use campaign name from multiCampaigns if available
        const campaignInfo = multiCampaigns?.find(mc => mc.id === group.campaign_id);
        if (campaignInfo) {
          group.name = campaignInfo.name;
          group.description = campaignInfo.description;
          group.execution_mode = campaignInfo.execution_mode;
          // 🚀 Bei aggregierten Daten slot_count verwenden, sonst from multiCampaigns
          group.total_slots = group.is_aggregated ? (group.slot_count || campaignInfo.total_slots) : campaignInfo.total_slots;
          group.campaign_string_id = campaignInfo.campaign_id; // String ID für API
        } else {
          group.name = `Multi-Campaign ${group.campaign_id}`;
          group.execution_mode = 'unknown';
          // 🚀 Bei aggregierten Daten slot_count verwenden, sonst Anzahl der Slots
          group.total_slots = group.is_aggregated ? (group.slot_count || 0) : group.slots.length;
          group.campaign_string_id = null;
        }
      });
      
      // Add regular jobs
      items.push(...regularJobs.map(job => ({ ...job, type: 'job' })));
      
      // Add multi-campaign groups (from jobs)
      items.push(...Object.values(campaignGroups));
    }
    
    // Add multi-campaigns ohne Jobs (draft, pending, failed, etc.)
    if (multiCampaigns) {
      const existingCampaignIds = Object.keys(campaignGroups).map(id => parseInt(id));
      const standaloneCampaigns = multiCampaigns.filter(campaign => 
        !existingCampaignIds.includes(campaign.id)
      );
      
      standaloneCampaigns.forEach(campaign => {
        // 🎯 Segmented-Parallel Campaigns (ohne Jobs) richtig typisieren
        const isDraft = campaign.status === 'draft';
        const isRunningSegmented = ['running', 'processing', 'completed', 'failed', 'paused'].includes(campaign.status);
        
        items.push({
          type: isDraft ? 'multi-campaign-draft' : 'multi-campaign-group',  // ← Fix: Nicht immer draft!
          campaign_id: campaign.id,
          campaign_string_id: campaign.campaign_id,
          name: campaign.name,
          subject: campaign.name,
          description: campaign.description,
          status: campaign.status,
          total_slots: campaign.total_slots || 0,
          total_recipients: campaign.total_recipients || 0,
          total_sent: campaign.total_sent || 0,
          total_failed: campaign.total_failed || 0,
          execution_mode: campaign.execution_mode,
          created_at: campaign.created_at,
          slots: [],  // Segmented-Campaigns haben Slots in campaign_slots, nicht als Jobs
          is_aggregated: isRunningSegmented  // Zeige als aggregiert wenn nicht draft
        });
      });
    }
    
    // Sort by creation date
    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [jobs, multiCampaigns]);

  // Job action mutations
  const jobActionMutation = useMutation(
    async ({ jobId, action }) => {
      await axios.post(`/api/jobs/${jobId}/${action}`);
    },
    {
      onSuccess: (_, { action }) => {
        queryClient.invalidateQueries('jobs');
        toast.success(`Job erfolgreich ${action === 'start' ? 'gestartet' : 'gestoppt'}`);
      },
      onError: () => {
        toast.error('Fehler bei der Job-Aktion');
      }
    }
  );

  // Delete job mutation
  const deleteJobMutation = useMutation(
    async (jobId) => {
      await axios.delete(`/api/jobs/${jobId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('jobs');
        toast.success('Job erfolgreich gelöscht');
      },
      onError: () => {
        toast.error('Fehler beim Löschen des Jobs');
      }
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-4 md:p-6 border dark:border-dark-copper-dark">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <Mail className="h-5 w-5 md:h-6 md:w-6 mr-2 text-blue-600" />
              Mail Jobs
            </h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-200">
              Verwalten Sie Ihre E-Mail-Kampagnen und deren Status
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowMultiCampaignDashboard(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto"
            >
              <Layers className="h-4 w-4 mr-2" />
              <span className="text-sm md:text-base">Multi-Campaigns</span>
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowMultiCampaignCreator(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="text-sm md:text-base">Multi-Campaign</span>
              </button>
            )}
            <button
              onClick={() => setShowCreateJob(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="text-sm md:text-base">Einzelner Job</span>
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List - Mobile-Responsive */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {combinedItems && combinedItems.length > 0 ? (
          <div className="block">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-dark-surface">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                      Job / Campaign
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                      Fortschritt
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                      Erstellt
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-dark-copper-dark">
                  {combinedItems.map((item) => (
                    item.type === 'job' ? (
                      <JobRow 
                        key={item.job_id} 
                        job={item} 
                        onAction={(action) => jobActionMutation.mutate({ jobId: item.job_id, action })}
                        onDelete={() => deleteJobMutation.mutate(item.job_id)}
                        onView={() => navigate(`/jobs/${item.job_id}`)}
                        onReuse={() => setJobToReuse(item)}
                      />
                    ) : item.type === 'multi-campaign-draft' ? (
                      <MultiCampaignDraftRow 
                        key={item.campaign_id} 
                        campaign={item}
                        onStart={() => {
                          // Campaign starten
                          axios.post(`/api/multi-campaigns/${item.campaign_string_id}/start`)
                            .then(() => {
                              toast.success('Multi-Campaign gestartet!');
                              queryClient.invalidateQueries('jobs');
                              queryClient.invalidateQueries('multiCampaigns');
                            })
                            .catch(() => toast.error('Fehler beim Starten'));
                        }}
                      />
                    ) : (
                      <MultiCampaignGroupRow 
                        key={item.campaign_id} 
                        campaignGroup={item}
                        onSlotAction={(jobId, action) => jobActionMutation.mutate({ jobId, action })}
                        onSlotDelete={(jobId) => deleteJobMutation.mutate(jobId)}
                        onSlotView={(jobId, campaignStringId) => {
                          if (jobId) {
                            navigate(`/jobs/${jobId}`);
                          } else if (campaignStringId) {
                            // Segmented-Parallel: Zur Campaign-Details
                            navigate(`/multi-campaigns/${campaignStringId}`);
                          }
                        }}
                      />
                    )
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Cards */}
            <div className="lg:hidden bg-white dark:bg-dark-secondary rounded-lg border dark:border-dark-copper-dark overflow-hidden">
              {combinedItems.map((item) => (
                item.type === 'job' ? (
                  <JobCard 
                    key={item.job_id} 
                    job={item} 
                    onAction={(action) => jobActionMutation.mutate({ jobId: item.job_id, action })}
                    onDelete={() => deleteJobMutation.mutate(item.job_id)}
                    onView={() => navigate(`/jobs/${item.job_id}`)}
                    onReuse={() => setJobToReuse(item)}
                  />
                ) : item.type === 'multi-campaign-draft' ? (
                  <MultiCampaignDraftCard 
                    key={item.campaign_id} 
                    campaign={item}
                    onStart={() => {
                      axios.post(`/api/multi-campaigns/${item.campaign_string_id}/start`)
                        .then(() => {
                          toast.success('Multi-Campaign gestartet!');
                          queryClient.invalidateQueries('jobs');
                          queryClient.invalidateQueries('multiCampaigns');
                        })
                        .catch(() => toast.error('Fehler beim Starten'));
                    }}
                  />
                ) : (
                  <MultiCampaignGroupCard 
                    key={item.campaign_id} 
                    campaignGroup={item}
                    onSlotAction={(jobId, action) => jobActionMutation.mutate({ jobId, action })}
                    onSlotView={(jobId) => navigate(`/jobs/${jobId}`)}
                  />
                )
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Jobs</h3>
            <p className="mt-1 text-sm text-gray-500">
              Erstellen Sie Ihren ersten E-Mail-Job
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateJob(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center mx-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ersten Job erstellen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Job Modal */}
      {(showCreateJob || jobToReuse) && (
        <CreateJobModal
          jobData={jobToReuse}
          onClose={() => {
            setShowCreateJob(false);
            setJobToReuse(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries('jobs');
            setShowCreateJob(false);
            setJobToReuse(null);
          }}
        />
      )}

      {/* Multi-Campaign Creator Modal */}
      {isAdmin && showMultiCampaignCreator && (
        <MultiCampaignCreatorModal
          onClose={() => setShowMultiCampaignCreator(false)}
          onSuccess={() => {
            setShowMultiCampaignCreator(false);
            queryClient.invalidateQueries('multiCampaigns');
            toast.success('Multi-Campaign erfolgreich erstellt!');
          }}
        />
      )}

      {/* Multi-Campaign Dashboard Modal */}
      {showMultiCampaignDashboard && (
        <MultiCampaignDashboardModal
          onClose={() => setShowMultiCampaignDashboard(false)}
        />
      )}
    </div>
  );
};

// Helper: Execution Mode Label
const getExecutionModeLabel = (mode) => {
  const labels = {
    'sequential': '📋 Sequential',
    'parallel': '⚡ Parallel',
    'segmented_parallel': '🎯 Provider-Segmented',
    'smtp_segmented_parallel': '📧 SMTP-Segmented',
    'scheduled': '⏰ Scheduled'
  };
  return labels[mode] || mode;
};

// Multi-Campaign Draft Row Component - Shows draft campaigns
const MultiCampaignDraftRow = ({ campaign, onStart }) => {
  const navigate = useNavigate();

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { color: 'bg-gray-100 text-gray-800', icon: Clock, text: 'Entwurf' },
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Wartend' },
      'running': { color: 'bg-blue-100 text-blue-800', icon: Play, text: 'Läuft' },
      'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Abgeschlossen' },
      'failed': { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Fehlgeschlagen' },
      'paused': { color: 'bg-orange-100 text-orange-800', icon: Pause, text: 'Pausiert' }
    };

    const config = statusConfig[status] || statusConfig['draft'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  return (
    <tr className="hover:bg-purple-50 dark:hover:bg-dark-surface cursor-pointer" onClick={() => navigate(`/multi-campaigns/${campaign.campaign_string_id}`)}>
      <td className="px-3 py-3">
        <div className="flex items-center">
          <Layers className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate max-w-xs">
              {campaign.name}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-300">
              {getExecutionModeLabel(campaign.execution_mode)} • {campaign.total_slots} Slots
            </div>
          </div>
        </div>
      </td>
      <td className="px-2 py-3">
        {getStatusBadge(campaign.status)}
      </td>
      <td className="px-2 py-3">
        <div className="w-24">
          <div className="text-xs text-gray-600">
            {campaign.total_sent || 0}/{campaign.total_recipients || 0}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-300">
            {campaign.status === 'completed' ? 'Abgeschlossen' : 
             campaign.status === 'running' ? 'Läuft...' :
             campaign.status === 'failed' ? 'Fehlgeschlagen' :
             'Noch nicht gestartet'}
          </div>
        </div>
      </td>
      <td className="px-2 py-3 text-xs text-gray-500 dark:text-gray-200">
        {new Date(campaign.created_at).toLocaleDateString('de-DE', { 
          day: '2-digit', 
          month: '2-digit' 
        })}
      </td>
      <td className="px-2 py-3 text-right">
        <div className="flex items-center justify-end space-x-2">
          {campaign.status === 'draft' || campaign.status === 'pending' ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStart();
            }}
            className="text-green-600 hover:text-green-700 text-xs px-2 py-1 rounded flex items-center space-x-1"
          >
            <Play className="h-3 w-3" />
            <span>Starten</span>
          </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/multi-campaigns/${campaign.campaign_string_id}`);
              }}
              className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded flex items-center space-x-1"
            >
              <Eye className="h-3 w-3" />
              <span>Details</span>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Multi-Campaign Group Row Component - Shows campaign with expandable slots
const MultiCampaignGroupRow = ({ campaignGroup, onSlotAction, onSlotDelete, onSlotView }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'processing': { color: 'bg-blue-100 text-blue-800', icon: Play },
      'running': { color: 'bg-blue-100 text-blue-800', icon: Play },
      'paused': { color: 'bg-orange-100 text-orange-800', icon: Pause },
      'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'failed': { color: 'bg-red-100 text-red-800', icon: XCircle },
      'cancelled': { color: 'bg-gray-100 text-gray-800', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.toUpperCase()}
      </span>
    );
  };

  const getProgressInfo = (campaignGroup) => {
    let total = campaignGroup.total_recipients || 0;
    let sent = campaignGroup.total_sent || 0;
    let failed = campaignGroup.total_failed || 0;
    
    // 🎯 Für Segmented-Parallel: Berechne aus Slots falls total_recipients = 0
    if (total === 0 && campaignGroup.slots && campaignGroup.slots.length > 0) {
      total = campaignGroup.slots.reduce((sum, slot) => sum + (slot.total_recipients || 0), 0);
      sent = campaignGroup.slots.reduce((sum, slot) => sum + (slot.sent_count || 0), 0);
      failed = campaignGroup.slots.reduce((sum, slot) => sum + (slot.failed_count || 0), 0);
    }
    
    const processed = sent + failed;
    const percentage = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

    return { total, sent, failed, processed, percentage };
  };

  const progress = getProgressInfo(campaignGroup);

  return (
    <>
      {/* Main Campaign Row */}
      <tr className="hover:bg-purple-50 dark:hover:bg-dark-surface cursor-pointer" onClick={() => navigate(`/multi-campaigns/${campaignGroup.campaign_string_id || campaignGroup.campaign_id}`)}>
        <td className="px-3 py-3">
          <div className="flex items-center">
            <Layers className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate max-w-xs">
                {campaignGroup.name}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-300">
                Multi-Campaign • {campaignGroup.is_aggregated ? (campaignGroup.slot_count || campaignGroup.total_slots || 0) : campaignGroup.slots.length} Slots • {campaignGroup.execution_mode}
              </div>
            </div>
          </div>
        </td>
        <td className="px-2 py-3">
          {getStatusBadge(campaignGroup.status)}
        </td>
        <td className="px-2 py-3">
          <div className="w-24">
            <div className="flex justify-between text-xs text-gray-600 dark:text-gray-200 mb-1">
              <span>{progress.percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-purple-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progress.percentage}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-300 mt-1">
              <span className="text-green-600">{progress.sent}</span>
              {progress.failed > 0 && <span className="text-red-600">{progress.failed}</span>}
            </div>
          </div>
        </td>
        <td className="px-2 py-3 text-xs text-gray-500 dark:text-gray-200">
          {new Date(campaignGroup.created_at).toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit' 
          })}
        </td>
        <td className="px-2 py-3 text-right">
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="text-purple-600 hover:text-purple-700 text-xs px-2 py-1 rounded"
            >
              {isExpanded ? 'Zuklappen' : 'Slots anzeigen'}
            </button>
          </div>
        </td>
      </tr>
      
      {/* Expanded Slot Rows */}
      {isExpanded && campaignGroup.slots.map((slotJob, index) => (
        <tr key={slotJob.job_id} className="bg-purple-50">
          <td className="px-6 py-2">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-xs font-medium text-purple-600">{index + 1}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-gray-700 truncate">
                  {slotJob.subject || `Slot ${index + 1}`}
                </div>
                <div className="text-xs text-gray-500 font-mono">
                  {slotJob.job_id.substring(0, 15)}...
                </div>
              </div>
            </div>
          </td>
          <td className="px-2 py-2">
            {getStatusBadge(slotJob.status)}
          </td>
          <td className="px-2 py-2">
            <div className="w-20">
              <div className="text-xs text-gray-600">
                {slotJob.sent_count || 0}/{slotJob.total_recipients || 0}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div 
                  className="bg-blue-500 h-1 rounded-full"
                  style={{ 
                    width: `${slotJob.total_recipients > 0 ? Math.round(((slotJob.sent_count || 0) / slotJob.total_recipients) * 100) : 0}%` 
                  }}
                ></div>
              </div>
            </div>
          </td>
          <td className="px-2 py-2 text-xs text-gray-500">
            {slotJob.emails_per_hour || 100}/h
          </td>
          <td className="px-2 py-2 text-right">
            <div className="flex items-center justify-end space-x-1">
              {slotJob.job_id ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                    onSlotView(slotJob.job_id, campaignGroup.campaign_string_id);
                }}
                className="text-blue-600 hover:text-blue-900 p-1"
                title="Slot Details"
              >
                <Eye className="h-3 w-3" />
              </button>
              ) : (
                <span className="text-xs text-gray-400">🤖</span>
              )}
              
              {slotJob.status === 'pending' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSlotAction(slotJob.job_id, 'start');
                  }}
                  className="text-green-600 hover:text-green-900 p-1"
                  title="Slot starten"
                >
                  <Play className="h-3 w-3" />
                </button>
              )}
              
              {(slotJob.status === 'processing' || slotJob.status === 'running') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSlotAction(slotJob.job_id, 'stop');
                  }}
                  className="text-red-600 hover:text-red-900 p-1"
                  title="Slot stoppen"
                >
                  <Pause className="h-3 w-3" />
                </button>
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
};

// Multi-Campaign Draft Card Component (Mobile)
const MultiCampaignDraftCard = ({ campaign, onStart }) => {
  const navigate = useNavigate();

  return (
    <div className="border-b border-gray-200 dark:border-dark-copper-dark p-4 hover:bg-purple-50 dark:hover:bg-dark-surface">
      {/* Header */}
      <div className="flex items-start justify-between mb-3" onClick={() => navigate(`/multi-campaigns/${campaign.campaign_string_id}`)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center mb-1">
            <Layers className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
              {campaign.name}
            </h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-300">
            Multi-Campaign • {campaign.total_slots} Slots • {campaign.execution_mode} • ENTWURF
          </p>
        </div>
        <div className="ml-2 flex-shrink-0">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Clock className="h-3 w-3 mr-1" />
            ENTWURF
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="mb-3">
        <div className="text-sm text-gray-600 dark:text-gray-200">
          {campaign.total_recipients} Empfänger • Noch nicht gestartet
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3">
        <div className="text-xs text-gray-500 dark:text-gray-300">
          {new Date(campaign.created_at).toLocaleDateString('de-DE')}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onStart();
          }}
          className="text-green-600 hover:text-green-700 text-xs px-3 py-1 rounded border border-green-200 flex items-center space-x-1"
        >
          <Play className="h-3 w-3" />
          <span>Starten</span>
        </button>
      </div>
    </div>
  );
};

// Multi-Campaign Group Card Component (Mobile)
const MultiCampaignGroupCard = ({ campaignGroup, onSlotAction, onSlotView }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'processing': { color: 'bg-blue-100 text-blue-800', icon: Play },
      'running': { color: 'bg-blue-100 text-blue-800', icon: Play },
      'paused': { color: 'bg-orange-100 text-orange-800', icon: Pause },
      'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'failed': { color: 'bg-red-100 text-red-800', icon: XCircle },
      'cancelled': { color: 'bg-gray-100 text-gray-800', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.toUpperCase()}
      </span>
    );
  };

  const totalSent = campaignGroup.total_sent || 0;
  const totalFailed = campaignGroup.total_failed || 0;
  const totalRecipients = campaignGroup.total_recipients || 0;
  const processed = totalSent + totalFailed;
  const percentage = totalRecipients > 0 ? Math.round((processed / totalRecipients) * 100) : 0;

  return (
    <div className="border-b border-gray-200 dark:border-dark-copper-dark p-4 hover:bg-purple-50 dark:hover:bg-dark-surface">
      {/* Header */}
      <div 
        className="flex items-start justify-between mb-3 cursor-pointer" 
        onClick={() => navigate(`/multi-campaigns/${campaignGroup.campaign_string_id || campaignGroup.campaign_id}`)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center mb-1">
            <Layers className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
              {campaignGroup.name}
            </h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-300">
            {getExecutionModeLabel(campaignGroup.execution_mode)} • {campaignGroup.slots.length} Slots
          </p>
        </div>
        <div className="ml-2 flex-shrink-0">
          {getStatusBadge(campaignGroup.status)}
        </div>
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-200 mb-1">
          <span>{processed} / {totalRecipients}</span>
          <span>{percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        <div className="flex justify-between mt-1">
          <div className="flex items-center text-xs text-green-600">
            <CheckCircle className="h-3 w-3 mr-1" />
            {totalSent}
          </div>
          {totalFailed > 0 && (
            <div className="flex items-center text-xs text-red-600">
              <XCircle className="h-3 w-3 mr-1" />
              {totalFailed}
            </div>
          )}
        </div>
      </div>

      {/* Expanded Slots */}
      {isExpanded && (
        <div className="mt-4 space-y-2">
          <div className="text-xs font-medium text-gray-700 mb-2">Slots:</div>
          {campaignGroup.slots.map((slotJob, index) => (
            <div key={slotJob.job_id} className="bg-purple-50 p-3 rounded border-l-4 border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                    <span className="text-xs font-medium text-purple-600">{index + 1}</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700">{slotJob.subject}</div>
                    <div className="text-xs text-gray-500 font-mono">{slotJob.job_id.substring(0, 20)}...</div>
                  </div>
                </div>
                {getStatusBadge(slotJob.status)}
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-600">
                  {slotJob.sent_count || 0}/{slotJob.total_recipients || 0} • {slotJob.emails_per_hour}/h
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => onSlotView(slotJob.job_id)}
                    className="text-blue-600 hover:text-blue-900 p-1"
                  >
                    <Eye className="h-3 w-3" />
                  </button>
                  {slotJob.status === 'pending' && (
                    <button
                      onClick={() => onSlotAction(slotJob.job_id, 'start')}
                      className="text-green-600 hover:text-green-900 p-1"
                    >
                      <Play className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div
        className="flex items-center justify-between mt-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="text-xs text-gray-500 dark:text-gray-300">
          {new Date(campaignGroup.created_at).toLocaleDateString('de-DE')}
        </div>
        <div className="text-xs text-purple-600">
          {isExpanded ? 'Zuklappen' : 'Slots anzeigen'}
        </div>
      </div>
    </div>
  );
};

// Job Row Component
const JobRow = ({ job, onAction, onDelete, onView, onReuse }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'processing': { color: 'bg-blue-100 text-blue-800', icon: Play },
      'running': { color: 'bg-blue-100 text-blue-800', icon: Play },
      'paused': { color: 'bg-orange-100 text-orange-800', icon: Pause },
      'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'failed': { color: 'bg-red-100 text-red-800', icon: XCircle },
      'cancelled': { color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
      'stopped': { color: 'bg-gray-100 text-gray-800', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.toUpperCase()}
      </span>
    );
  };

  const getProgressInfo = (job) => {
    const total = job.total_recipients || 0;
    const sent = job.sent_count || 0;
    const failed = job.failed_count || 0;
    const pending = job.actual_pending || Math.max(0, total - sent - failed);
    const processed = sent + failed;
    const percentage = total > 0 ? Math.round((processed / total) * 100) : 0;

    return { total, sent, failed, pending, processed, percentage };
  };

  const progress = getProgressInfo(job);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-dark-surface">
      <td className="px-3 py-3">
        <div className="flex items-center">
          <Mail className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate max-w-xs">
              {job.subject || 'Kein Betreff'}
            </div>
            <div className="text-xs text-gray-500 font-mono">
              {job.job_id.substring(0, 15)}...
            </div>
          </div>
        </div>
      </td>
      <td className="px-2 py-3">
        <div className="space-y-1">
        {getStatusBadge(job.status)}
          {/* 🤖 AI-Generation Status */}
          {job.ai_randomization_enabled && job.ai_generation_status && job.ai_generation_status !== 'completed' && (
            <div className="text-xs text-purple-600 flex items-center mt-1">
              <span className="animate-pulse">🤖</span>
              <span className="ml-1">
                AI-Gen: {job.ai_generation_progress || 0}/{job.ai_generation_total || 0}
              </span>
            </div>
          )}
          {job.ai_randomization_enabled && job.ai_generation_status === 'completed' && (
            <div className="text-xs text-green-600 flex items-center mt-1">
              <span>✅</span>
              <span className="ml-1">
                AI: {job.ai_generation_total} Versionen
              </span>
            </div>
          )}
        </div>
      </td>
      <td className="px-2 py-3">
        <div className="w-24">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-200 mb-1">
            <span>{progress.percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-300 mt-1">
            <span className="text-green-600">{progress.sent}</span>
            {progress.failed > 0 && <span className="text-red-600">{progress.failed}</span>}
          </div>
        </div>
      </td>
      <td className="px-2 py-3 text-xs text-gray-500 dark:text-gray-200">
        {new Date(job.created_at).toLocaleDateString('de-DE', { 
          day: '2-digit', 
          month: '2-digit' 
        })}
      </td>
      <td className="px-2 py-3 text-right">
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={onReuse}
            className="text-green-600 hover:text-green-900"
            title="Job wiederverwenden"
          >
            <Copy className="h-4 w-4" />
          </button>
          
          <button
            onClick={onView}
            className="text-blue-600 hover:text-blue-900"
            title="Details anzeigen"
          >
            <Eye className="h-4 w-4" />
          </button>
          
          {job.status === 'pending' && (
            <button
              onClick={() => onAction('start')}
              className={`${job.ai_randomization_enabled && job.ai_generation_status !== 'completed' ? 'text-gray-400 cursor-not-allowed' : 'text-green-600 hover:text-green-900'}`}
              title={job.ai_randomization_enabled && job.ai_generation_status !== 'completed' ? '⏳ AI-Generation läuft noch...' : 'Job starten'}
              disabled={job.ai_randomization_enabled && job.ai_generation_status !== 'completed'}
            >
              <Play className="h-4 w-4" />
            </button>
          )}
          
          {job.status === 'paused' && (
            <button
              onClick={() => onAction('start')}
              className="text-blue-600 hover:text-blue-900"
              title="Job fortsetzen"
            >
              <Play className="h-4 w-4" />
            </button>
          )}
          
          {(job.status === 'processing' || job.status === 'running') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction('stop');
              }}
              className="text-red-600 hover:text-red-900"
              title="Job pausieren"
            >
              <Pause className="h-4 w-4" />
            </button>
          )}
          
          {(job.status === 'pending' || job.status === 'paused' || job.status === 'failed') && (
            <button
              onClick={onDelete}
              className="text-red-600 hover:text-red-900"
              title="Job löschen"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Enhanced Create Job Modal with recipient filtering
const CreateJobModal = ({ jobData, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [jobDetails, setJobDetails] = useState(null);

  // Load full job details when jobData is provided
  React.useEffect(() => {
    if (jobData && jobData.job_id) {
      loadJobDetails(jobData.job_id);
    }
  }, [jobData]);

  const loadJobDetails = async (jobId) => {
    try {
      const response = await axios.get(`/api/jobs/${jobId}`);
      setJobDetails(response.data.data);
    } catch (error) {
      console.error('Failed to load job details:', error);
    }
  };

  const handleCreateJob = () => {
    if (jobData && jobDetails) {
      // 🔧 Store job data in sessionStorage (URL zu kurz für große HTMLs!)
      const jobCopyData = {
        subject: jobDetails.job.subject || '',
        message: jobDetails.job.message || '',  // Komplettes HTML
        from_name: jobDetails.job.from_name || '',
        dynamic_from_email: jobDetails.job.dynamic_from_email || '',
        use_dynamic_from_email: jobDetails.job.use_dynamic_from_email || false,
        reply_to_email: jobDetails.job.reply_to_email || '',
        emails_per_hour: jobDetails.job.emails_per_hour || 100,
        is_html: jobDetails.job.is_html || false,
        ai_randomization_enabled: jobDetails.job.ai_randomization_enabled || false,
        ai_randomization_frequency: jobDetails.job.ai_randomization_frequency || null,
        list_unsubscribe_enabled: jobDetails.job.list_unsubscribe_enabled || false,
        list_unsubscribe_url: jobDetails.job.list_unsubscribe_url || '{Domain}/unsubscribe',
        list_unsubscribe_method: jobDetails.job.list_unsubscribe_method || 'oneclick',
        source_job_id: jobData.job_id,
        recipient_filter: recipientFilter
      };
      
      sessionStorage.setItem('jobCopyData', JSON.stringify(jobCopyData));
      navigate('/?reuse=true');
    } else {
      navigate('/');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-secondary rounded-lg p-6 max-w-md w-full mx-4 border dark:border-dark-copper-mid">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {jobData ? 'Job wiederverwenden' : 'Neuen Job erstellen'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="py-4">
          {jobData ? (
            <>
              <div className="text-center mb-6">
                <Copy className="mx-auto h-12 w-12 text-green-500 mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  Job wiederverwenden
                </h4>
                <p className="text-gray-600 mb-2">
                  <strong>Betreff:</strong> {jobData.subject && jobData.subject.length > 60 
                    ? `${jobData.subject.substring(0, 60)}...` 
                    : jobData.subject}
                </p>
                <p className="text-sm text-gray-500">
                  HTML-Inhalt und Einstellungen werden übernommen
                </p>
              </div>

              {/* Recipient Filter Options */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Welche Empfänger sollen verwendet werden?
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="recipientFilter"
                      value="all"
                      checked={recipientFilter === 'all'}
                      onChange={(e) => setRecipientFilter(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Alle ursprünglichen Empfänger
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="recipientFilter"
                      value="pending"
                      checked={recipientFilter === 'pending'}
                      onChange={(e) => setRecipientFilter(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Nur ausstehende Empfänger (Pending)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="recipientFilter"
                      value="failed"
                      checked={recipientFilter === 'failed'}
                      onChange={(e) => setRecipientFilter(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Nur fehlgeschlagene Empfänger (Failed)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="recipientFilter"
                      value="pending_failed"
                      checked={recipientFilter === 'pending_failed'}
                      onChange={(e) => setRecipientFilter(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Pending + Failed Empfänger
                    </span>
                  </label>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Mail className="mx-auto h-12 w-12 text-blue-500 mb-4" />
              <p className="text-gray-600 mb-6">
                Verwenden Sie das Dashboard, um einen neuen Job zu erstellen.
              </p>
            </div>
          )}
          
          <div className="flex space-x-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCreateJob}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {jobData ? 'Job wiederverwenden' : 'Zum Dashboard'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mobile Job Card Component
const JobCard = ({ job, onAction, onDelete, onView, onReuse }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Wartend' },
      processing: { color: 'bg-blue-100 text-blue-800', icon: Play, text: 'Läuft' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Fertig' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Fehler' },
      paused: { color: 'bg-gray-100 text-gray-800', icon: Pause, text: 'Pausiert' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const getProgressInfo = (job) => {
    const total = job.total_recipients || job.recipient_count || 0;
    const sent = job.sent_count || 0;
    const failed = job.failed_count || 0;
    const processed = sent + failed;
    const pending = Math.max(0, total - processed);
    const percentage = total > 0 ? Math.round((processed / total) * 100) : 0;

    return { total, sent, failed, processed, pending, percentage };
  };

  const progress = getProgressInfo(job);

  return (
    <div 
      className="border-b border-gray-200 dark:border-dark-copper-dark p-4 hover:bg-gray-50 dark:hover:bg-dark-surface cursor-pointer"
      onClick={() => onView()}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center mb-1">
            <Mail className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
              {job.subject || 'Kein Betreff'}
            </h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-300 font-mono">
            {job.job_id.substring(0, 25)}...
          </p>
        </div>
        <div className="ml-2 flex-shrink-0">
          {getStatusBadge(job.status)}
        </div>
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-200 mb-1">
          <span>{progress.processed} / {progress.total}</span>
          <span>{progress.percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress.percentage}%` }}
          ></div>
        </div>
        <div className="flex justify-between mt-1">
          <div className="flex items-center text-xs text-green-600">
            <CheckCircle className="h-3 w-3 mr-1" />
            {progress.sent}
          </div>
          {progress.failed > 0 && (
            <div className="flex items-center text-xs text-red-600">
              <XCircle className="h-3 w-3 mr-1" />
              {progress.failed}
            </div>
          )}
          <div className="text-xs text-gray-500 dark:text-gray-300">
            {progress.pending} ausstehend
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center text-xs text-gray-500">
          <Calendar className="h-3 w-3 mr-1" />
          {new Date(job.created_at).toLocaleDateString('de-DE')}
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReuse();
            }}
            className="text-green-600 hover:text-green-900 p-1"
            title="Wiederverwenden"
          >
            <Copy className="h-4 w-4" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
            className="text-blue-600 hover:text-blue-900 p-1"
            title="Details"
          >
            <Eye className="h-4 w-4" />
          </button>
          
          {job.status === 'pending' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction('start');
              }}
              className="text-green-600 hover:text-green-900 p-1"
              title="Starten"
            >
              <Play className="h-4 w-4" />
            </button>
          )}
          
          {job.status === 'processing' && (
            <button
              onClick={() => onAction('stop')}
              className="text-red-600 hover:text-red-900 p-1"
              title="Stoppen"
            >
              <Pause className="h-4 w-4" />
            </button>
          )}
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-red-600 hover:text-red-900 p-1"
            title="Löschen"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Multi-Campaign Creator Modal Wrapper
const MultiCampaignCreatorModal = ({ onClose, onSuccess }) => {
  return (
    <MultiCampaignCreator
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

// Multi-Campaign Dashboard Modal Wrapper
const MultiCampaignDashboardModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-secondary rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden border dark:border-dark-copper-mid">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <Layers className="h-6 w-6 mr-2 text-purple-600 dark:text-dark-copper-glow" />
            Multi-Campaign Dashboard
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[80vh]">
          <MultiCampaignDashboard />
        </div>
      </div>
    </div>
  );
};

export default MailJobs;
